/**
 * Friend Inbox model — explicit typed state definition.
 *
 * Elm-ish architecture: state is explicit, transitions are pure,
 * effects are isolated. There is no implicit state machine encoded
 * in useState hooks in the page component.
 *
 * Domain: a Friend logs in and inspects incoming intro requests.
 * The Friend can accept or reject a pending request; Sponsor-side
 * decisions are visible to the Friend only via status transitions.
 *
 * Security posture:
 * - Friend role is the only role permitted to interact with this
 *   feature (server-side enforced; UI is for affordance only).
 * - Accept/Reject endpoints are server-authoritative for BOLA.
 * - We never display legal names, addresses, or contact details
 *   here; the model carries only the canonical server response.
 */
import type {
  IntroDetailResponse,
  IntroInboxItem,
  IntroInboxResponse,
  RecipientIntroStatus,
} from '@/api/introRequests';

/**
 * Loading lifecycle for the inbox list.
 *
 *   - 'idle'    : initial, no load has been attempted yet
 *   - 'loading' : an effect is in flight
 *   - 'ready'   : list contains at least one item
 *   - 'empty'   : list is empty (server returned zero items)
 *   - 'error'   : an error occurred; model.error is set
 */
export type LoadLifecycle = 'idle' | 'loading' | 'ready' | 'empty' | 'error';

/**
 * Snapshot of an in-flight accept/reject call so the UI can disable
 * the corresponding button. The server is authoritative; this flag
 * only prevents duplicate clicks during the in-flight window.
 */
export type TransitionTarget = 'accept' | 'reject' | null;

/**
 * Friend Inbox model — single source of truth per page.
 */
export interface FriendInboxModel {
  readonly load: LoadLifecycle;
  readonly items: ReadonlyArray<IntroInboxItem>;
  readonly selected: IntroDetailResponse | null;
  readonly transitioning: TransitionTarget;
  readonly error: string | null;
  /** monotonically increasing; bumped on every refresh so effects can short-circuit stale results. */
  readonly refreshToken: number;
}

/**
 * Build the initial model.
 */
export function initialFriendInboxModel(): FriendInboxModel {
  return {
    load: 'idle',
    items: [],
    selected: null,
    transitioning: null,
    error: null,
    refreshToken: 0,
  };
}

/**
 * Canonical status helper: narrow IntroRequestStatus to the set
 * actually relevant to the Friend inbox. We do not interpret the
 * status here; the server has already decided it.
 */
export function isPendingStatus(s: RecipientIntroStatus): boolean {
  return s === 'pending';
}

/**
 * Apply a list response to the model. Pure; the caller passes in a
 * refresh token so old responses can be discarded by the caller.
 */
export function applyListResponse(
  model: FriendInboxModel,
  response: IntroInboxResponse,
): FriendInboxModel {
  return {
    ...model,
    items: response.items,
    load: response.items.length === 0 ? 'empty' : 'ready',
    error: null,
  };
}

/**
 * Apply a single-item detail response. If the model has the item in
 * the inbox list, we also patch its row.
 */
export function applyDetailResponse(
  model: FriendInboxModel,
  detail: IntroDetailResponse,
): FriendInboxModel {
  const items = model.items.map((it) =>
    it.id === detail.id ? { ...it, status: detail.status } : it,
  );
  return { ...model, items, selected: detail, error: null };
}

/**
 * Mark a transition as in-flight.
 */
export function beginTransition(
  model: FriendInboxModel,
  target: TransitionTarget,
): FriendInboxModel {
  return { ...model, transitioning: target, error: null };
}

/**
 * Apply a transition result and clear the in-flight flag.
 */
export function endTransition(
  model: FriendInboxModel,
  detail: IntroDetailResponse,
): FriendInboxModel {
  const items = model.items.map((it) =>
    it.id === detail.id ? { ...it, status: detail.status } : it,
  );
  const selected =
    model.selected && model.selected.id === detail.id ? detail : model.selected;
  return { ...model, items, selected, transitioning: null, error: null };
}

/**
 * Record an error and clear any in-flight flag.
 */
export function recordError(model: FriendInboxModel, message: string): FriendInboxModel {
  return { ...model, error: message, transitioning: null };
}

/**
 * Set the load lifecycle to loading and bump the refresh token so any
 * in-flight stale response can be discarded by the effect runner.
 */
export function beginLoad(model: FriendInboxModel): FriendInboxModel {
  return { ...model, load: 'loading', refreshToken: model.refreshToken + 1 };
}

/**
 * Clear the selected detail (Friend closed the detail panel).
 */
export function clearSelection(model: FriendInboxModel): FriendInboxModel {
  return { ...model, selected: null };
}
